// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The receipt for the newest row on the append-only content-review log — who, when, which team,
// which exact hashes, which checklist ticks, and the free-text note.
//
// reporting_approvals is append-only four ways (SELECT-only RLS, a no-mutate trigger, a TRUNCATE
// guard, a REVOKE), so a withdrawal is a NEWER ROW rather than a deletion. That is why this
// renders a single "newest row decides" receipt with an explicit withdrawn state, instead of
// pretending an absent review and a withdrawn review are the same thing — they are not, and the
// difference is exactly what a regulator would ask about.

import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import type { NewsletterReviewRow } from "@/hooks/investment-reporting/useNewsletterReview";
import { newsletterCheckLabel, shortHash } from "./labels";

function formatStamp(t: string | null | undefined): string {
  if (!t) return "—";
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return String(t);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ContentReviewReceipt({
  review,
  className,
}: {
  review: NewsletterReviewRow | null | undefined;
  className?: string;
}) {
  if (!review || !review.action) {
    return (
      <Card className={cn("p-4", className)}>
        <div className="flex items-start gap-2">
          <Clock className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
          <p className="font-body text-sm text-muted-foreground">
            No content review has ever been recorded for these bytes. Absence is a refusal, not a
            pending state — the newsletter cannot be reserved for send.
          </p>
        </div>
      </Card>
    );
  }

  const withdrawn = review.action === "revoke_content_review";
  const ticks = Object.entries((review.checklist ?? {}) as Record<string, unknown>).filter(
    ([, v]) => v === true,
  );

  return (
    <Card className={cn("p-4 space-y-2", className)}>
      <div className="flex items-start gap-2">
        {withdrawn ? (
          <XCircle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden />
        ) : (
          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden />
        )}
        <div className="min-w-0 space-y-1">
          <div className="font-body text-sm font-medium text-foreground">
            {withdrawn ? "Content review WITHDRAWN" : "Content review recorded"}
          </div>
          <div className="font-body text-[11px] text-muted-foreground break-all">
            {review.actor_team ? `${review.actor_team} · ` : ""}
            actor {shortHash(review.actor, 8)} · {formatStamp(review.at)}
          </div>
          <div className="font-body text-[11px] text-muted-foreground break-all">
            bound to IR {shortHash(review.ir_hash, 12)} · envelope {shortHash(review.envelope_sha256, 12)}
          </div>
          {review.note && (
            <p className="font-body text-xs text-foreground whitespace-pre-wrap">{review.note}</p>
          )}
        </div>
      </div>

      {!withdrawn && ticks.length > 0 && (
        <ul className="font-body text-[11px] text-muted-foreground space-y-0.5 pl-6">
          {ticks.map(([k]) => (
            <li key={k} className="flex items-start gap-1.5">
              <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0 text-primary" aria-hidden />
              <span>{newsletterCheckLabel(k)}</span>
            </li>
          ))}
        </ul>
      )}

      {withdrawn && (
        <p className="font-body text-[11px] text-muted-foreground pl-6">
          Nothing was deleted — the withdrawal is a newer row on the append-only log, and the newest
          row decides. The send refuses again until a fresh review is recorded.
        </p>
      )}
    </Card>
  );
}
